"use client"
import { Link } from "react-router-dom"

const TermsOfService = () => {
  return (
    <div className="terms-page">
      {/* Hero Section */}
      <section className="terms-hero">
        <div className="terms-hero-container">
          <h1 className="terms-hero-title">Terms of Service</h1>
          <p className="terms-hero-description">Last updated: May 1, 2023</p>
        </div>
      </section>

      {/* Terms Content */}
      <section className="terms-content-section">
        <div className="terms-content-container">
          <div className="terms-content">
            <div className="terms-section">
              <h2 className="section-title">1. Introduction</h2>
              <p>
                Welcome to Shopify Scraper ("we," "our," or "us"). These Terms of Service ("Terms") govern your access
                to and use of the Shopify Scraper website, Chrome extension, and services (collectively, the "Service").
              </p>
              <p>
                By accessing or using the Service, you agree to be bound by these Terms. If you disagree with any part
                of the Terms, you may not access the Service.
              </p>
            </div>

            <div className="terms-section">
              <h2 className="section-title">2. Use of the Service</h2>
              <p>
                Shopify Scraper provides tools for extracting publicly available product data from Shopify stores. By
                using our Service, you agree to:
              </p>
              <ul>
                <li>Use the Service only for lawful purposes and in accordance with these Terms.</li>
                <li>
                  Not use the Service in any way that violates any applicable federal, state, local, or international
                  law or regulation.
                </li>
                <li>
                  Not use the Service to infringe upon intellectual property rights or violate the terms of service of
                  any website you access.
                </li>
                <li>
                  Not attempt to gain unauthorized access to any part of the Service, other accounts, or computer
                  systems or networks connected to the Service.
                </li>
                <li>
                  Not use the Service in a manner that could disable, overburden, damage, or impair the site or
                  interfere with any other party's use of the Service.
                </li>
              </ul>
            </div>

            <div className="terms-section">
              <h2 className="section-title">3. Accounts</h2>
              <p>
                When you create an account with us, you must provide accurate, complete, and up-to-date information. You
                are responsible for safeguarding the password that you use to access the Service and for any activities
                or actions under your password.
              </p>
              <p>
                You agree not to disclose your password to any third party. You must notify us immediately upon becoming
                aware of any breach of security or unauthorized use of your account.
              </p>
            </div>

            <div className="terms-section">
              <h2 className="section-title">4. Subscription and Payments</h2>
              <p>
                Some features of the Service require a subscription. By subscribing to a paid plan, you agree to pay the
                subscription fees as described at the time of purchase.
              </p>
              <p>
                Subscription fees are billed in advance on a monthly or annual basis, depending on the billing cycle you
                select. Unless you cancel your subscription before the end of the current billing period, we will
                automatically renew your subscription and charge the subscription fee to your payment method.
              </p>
              <p>
                You can cancel your subscription at any time through your account settings. Upon cancellation, your
                subscription will remain active until the end of the current billing period.
              </p>
            </div>

            <div className="terms-section">
              <h2 className="section-title">5. Refunds</h2>
              <p>
                We offer a 14-day money-back guarantee for all paid plans. If you are not satisfied with our Service,
                you can request a full refund within 14 days of your purchase by contacting our support team.
              </p>
              <p>
                After the 14-day period, subscriptions are non-refundable, and there will be no refunds or credits for
                partial months of service, upgrade/downgrade refunds, or refunds for months unused with an open account.
              </p>
            </div>

            <div className="terms-section">
              <h2 className="section-title">6. Intellectual Property</h2>
              <p>
                The Service and its original content, features, and functionality are and will remain the exclusive
                property of Shopify Scraper and its licensors. The Service is protected by copyright, trademark, and
                other laws of both the United States and foreign countries.
              </p>
              <p>
                Our trademarks and trade dress may not be used in connection with any product or service without the
                prior written consent of Shopify Scraper.
              </p>
            </div>

            <div className="terms-section">
              <h2 className="section-title">7. User Data</h2>
              <p>
                We collect and process personal data as described in our Privacy Policy. By using the Service, you
                consent to such processing and you warrant that all data provided by you is accurate.
              </p>
            </div>

            <div className="terms-section">
              <h2 className="section-title">8. Limitation of Liability</h2>
              <p>
                In no event shall Shopify Scraper, nor its directors, employees, partners, agents, suppliers, or
                affiliates, be liable for any indirect, incidental, special, consequential or punitive damages,
                including without limitation, loss of profits, data, use, goodwill, or other intangible losses,
                resulting from:
              </p>
              <ul>
                <li>Your access to or use of or inability to access or use the Service;</li>
                <li>Any conduct or content of any third party on the Service;</li>
                <li>Any content obtained from the Service; and</li>
                <li>Unauthorized access, use or alteration of your transmissions or content.</li>
              </ul>
            </div>

            <div className="terms-section">
              <h2 className="section-title">9. Disclaimer</h2>
              <p>
                Your use of the Service is at your sole risk. The Service is provided on an "AS IS" and "AS AVAILABLE"
                basis. The Service is provided without warranties of any kind, whether express or implied, including,
                but not limited to, implied warranties of merchantability, fitness for a particular purpose,
                non-infringement or course of performance.
              </p>
            </div>

            <div className="terms-section">
              <h2 className="section-title">10. Changes to Terms</h2>
              <p>
                We reserve the right, at our sole discretion, to modify or replace these Terms at any time. If a
                revision is material, we will try to provide at least 30 days' notice prior to any new terms taking
                effect. What constitutes a material change will be determined at our sole discretion.
              </p>
              <p>
                By continuing to access or use our Service after those revisions become effective, you agree to be bound
                by the revised terms. If you do not agree to the new terms, please stop using the Service.
              </p>
            </div>

            <div className="terms-section">
              <h2 className="section-title">11. Contact Us</h2>
              <p>
                If you have any questions about these Terms, please{" "}
                <Link to="/contact" className="terms-link">
                  contact us
                </Link>
                .
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Styling */}
      <style jsx="true">{`
        /* Terms Hero Section */
        .terms-hero {
          background-color: #007BFF;
          color: white;
          padding: 5rem 0;
          text-align: center;
        }
        
        .terms-hero-container {
          max-width: 1200px;
          margin: 0 auto;
          padding: 0 1.5rem;
        }
        
        .terms-hero-title {
          font-size: 2.5rem;
          font-weight: 700;
          margin-bottom: 1.5rem;
        }
        
        .terms-hero-description {
          font-size: 1.25rem;
          opacity: 0.9;
        }
        
        /* Terms Content Section */
        .terms-content-section {
          padding: 5rem 0;
          background-color: #f9fafb;
        }
        
        .terms-content-container {
          max-width: 1200px;
          margin: 0 auto;
          padding: 0 1.5rem;
        }
        
        .terms-content {
          background-color: white;
          padding: 3rem;
          border-radius: 0.5rem;
          box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
        }
        
        .terms-section {
          margin-bottom: 3rem;
        }
        
        .section-title {
          font-size: 1.5rem;
          font-weight: 700;
          margin-bottom: 1.5rem;
          color: #1a202c;
        }
        
        .terms-section p {
          margin-bottom: 1rem;
          line-height: 1.6;
          color: #4a5568;
        }
        
        .terms-section ul {
          margin-bottom: 1rem;
          padding-left: 1.5rem;
        }
        
        .terms-section li {
          margin-bottom: 0.5rem;
          line-height: 1.6;
          color: #4a5568;
        }
        
        .terms-link {
          color: #007BFF;
          text-decoration: none;
          border-bottom: 1px solid #007BFF;
          transition: border-color 0.2s;
        }
        
        .terms-link:hover {
          border-color: transparent;
        }
        
        /* Responsive Styles */
        @media (max-width: 768px) {
          .terms-hero-title {
            font-size: 2rem;
          }
          
          .terms-hero-description {
            font-size: 1.125rem;
          }
          
          .terms-content {
            padding: 1.5rem;
          }
          
          .section-title {
            font-size: 1.25rem;
          }
        }
      `}</style>
    </div>
  )
}

export default TermsOfService
